import cx from 'classnames';
import React, { useContext } from 'react';
import type { ToastOptions } from 'react-toastify';
import { ToastContainer, toast } from 'react-toastify';

import 'react-toastify/dist/ReactToastify.css';
import ToastMessage from './message';
import styles from './toastr.module.scss';

interface ToastProps extends ToastOptions {
  details?: React.ReactNode;
  className?: string;
}

interface ToastrContextType {
  show: (
    message: React.ReactNode,
    { details, className, ...options }?: ToastProps
  ) => void;
  success: (
    message: React.ReactNode,
    { details, className, ...options }?: ToastProps
  ) => void;
  error: (
    message: React.ReactNode,
    { details, className, ...options }?: ToastProps
  ) => void;
  warn: (
    message: React.ReactNode,
    { details, className, ...options }?: ToastProps
  ) => void;
  announce: (message: React.ReactNode) => void;
}
const ToastrContext = React.createContext<ToastrContextType | null>(null);

export const useToastr = () => {
  const toastrContext = useContext(ToastrContext);

  if (!toastrContext) {
    throw new Error('useToastr has to be used within <ToastrContext.Provider>');
  }

  return toastrContext;
};

export function Toastr({ children }: { children: React.ReactElement }) {
  const classes = {
    success: styles.success,
    error: styles.error,
    warning: styles.warning,
    info: styles.info,
  };

  const show: ToastrContextType['show'] = (
    message,
    { details, className, ...options } = {}
  ) => {
    const opt = {
      ...options,
      className: cx(className || classes.info, styles.toastr),
    };
    toast(<ToastMessage message={message} details={details} />, opt);
  };

  return (
    <>
      <ToastrContext.Provider
        value={{
          show,
          success: (content, args = {}) =>
            show(content, {
              ...args,
              className: cx(args?.className, classes.success),
            }),
          error: (content, args = {}) =>
            show(content, {
              ...args,
              className: cx(args?.className, classes.error),
            }),
          warn: (content, args = {}) =>
            show(content, {
              ...args,
              className: cx(args?.className, classes.warning),
            }),
          announce: (content) =>
            toast(content, { className: styles.announcement }),
        }}
      >
        {children}
      </ToastrContext.Provider>
      <ToastContainer
        position="top-right"
        closeButton={false}
        autoClose={4000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
      />
    </>
  );
}
